import { ReactNode } from 'react'

export type SubtextVisibility = 'always' | 'hover' | 'focus' | 'hoverOrFocus' | 'never'

export interface SubtextState {
	focused: boolean
	hovered: boolean
	disabled: boolean
	value?: string
}

export interface SubtextResult {
	text?: ReactNode
	visibility?: SubtextVisibility
}

export type SubtextStrategy = (state: SubtextState) => SubtextResult

export interface SubtextRule {
	when: (state: SubtextState) => boolean
	text: ReactNode
	visibility?: SubtextVisibility
}

// Helper functions for common subtext patterns
export const subtextHelpers = {
	always: (text: ReactNode): SubtextResult => ({ text, visibility: 'always' }),
	hover: (text: ReactNode): SubtextResult => ({ text, visibility: 'hover' }),
	focus: (text: ReactNode): SubtextResult => ({ text, visibility: 'focus' }),
	hoverOrFocus: (text: ReactNode): SubtextResult => ({ text, visibility: 'hoverOrFocus' }),
	when: (predicate: (s: SubtextState) => boolean, text: ReactNode, visibility: SubtextVisibility = 'hoverOrFocus'): SubtextRule => ({
		when: predicate,
		text,
		visibility,
	}),
	shouldShow: (result: SubtextResult, state: SubtextState): boolean => {
		if (!result.text || result.visibility === 'never') return false
		if (result.visibility === 'always') return true
		if (result.visibility === 'hover') return state.hovered
		if (result.visibility === 'focus') return state.focused
		if (result.visibility === 'hoverOrFocus') return state.hovered || state.focused
		return false
	},
}

// Hook for rule-based subtexts
export function useFieldSubtext(rules: SubtextRule[], state: SubtextState): SubtextResult {
	const matchingRule = rules.find(rule => rule.when(state))
	return matchingRule 
		? { text: matchingRule.text, visibility: matchingRule.visibility }
		: { text: undefined, visibility: 'never' }
}
